/**
 * FeedbackButtons – tumme upp/ned för AI-svar.
 * Idempotent: kan bara byta mellan up/down, inte skicka flera gånger med samma val.
 * Skickar GA4 custom event 'ai_feedback' vid klick för spårning.
 */
import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function FeedbackButtons({ kind, sessionId, messageIndex, sfs, chapter, paragraph }) {
  const [state, setState] = useState(null); // "up" | "down" | null
  const [sending, setSending] = useState(false);

  const submit = async (feedback) => {
    if (sending || state === feedback) return;
    setSending(true);
    const prev = state;
    setState(feedback); // optimistisk uppdatering
    try {
      await api.post("/laws/feedback", {
        kind,
        feedback,
        session_id: sessionId,
        message_index: messageIndex,
        sfs,
        chapter,
        paragraph,
      });
      // GA4-event så vi kan mäta i Analytics
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "ai_feedback", {
          feedback_kind: kind,
          feedback_value: feedback,
          paragraph: paragraph ? `${sfs}-${chapter}-${paragraph}` : undefined,
        });
      }
    } catch {
      setState(prev);
      toast.error("Kunde inte spara feedback – försök igen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      <span className="text-[10px] text-zinc-400 uppercase tracking-wider">Hjälpsamt?</span>
      <button
        onClick={() => submit("up")}
        disabled={sending}
        data-testid={`feedback-up-${paragraph || "explain"}-${messageIndex ?? 0}`}
        className={`p-1 rounded-sm transition ${
          state === "up"
            ? "bg-emerald-100 text-emerald-700"
            : "text-zinc-400 hover:text-emerald-600 hover:bg-zinc-100"
        }`}
        aria-label="Bra svar"
        title={state === "up" ? "Tack för din feedback!" : "Bra svar"}
      >
        {state === "up" ? <Check className="w-3 h-3" /> : <ThumbsUp className="w-3 h-3" />}
      </button>
      <button
        onClick={() => submit("down")}
        disabled={sending}
        data-testid={`feedback-down-${paragraph || "explain"}-${messageIndex ?? 0}`}
        className={`p-1 rounded-sm transition ${
          state === "down"
            ? "bg-rose-100 text-rose-700"
            : "text-zinc-400 hover:text-rose-600 hover:bg-zinc-100"
        }`}
        aria-label="Dåligt svar"
        title={state === "down" ? "Tack för din feedback!" : "Dåligt svar"}
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
    </div>
  );
}
